import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
  HttpResponse,
  HttpStatusCode,
} from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { delay, dematerialize, materialize, mergeMap } from 'rxjs/operators';
import { environment } from '@environments/environment';
import {
  AmountLeftHint,
  CampaignTypes,
  ENVIRONMENTS,
  RETAILERS,
  SubType,
} from '@shared/constants';
import {
  CouponsResponseParams,
  FullResponseParams,
  generateCampaigns,
  generateChallenges,
  generateCoupons,
  generateEligibleProduct,
  generateProducts,
  generateRewards,
  getMonthDates,
} from '@shared/mocks';
import {
  ONBOARDING_AB_TEST_KEY,
  OnboardingAbTestVariant,
} from '@shared/models';

// Customer ids the mock uses to drive the state of the generated challenges
const STATUS_BY_CUSTOMER_ID: Record<string, 'noSelections' | 'allWon'> = {
  step1: 'noSelections',
  step3: 'allWon',
};

// Retailers whose builds set `hasCampaignBis` (see `ConfigLoaderService`). The
// static `environment` cannot answer this under Playwright, where the retailer
// comes from the query string, so it is read back from localStorage like the
// rest of the mock does.
const BIS_CAMPAIGN_RETAILERS: string[] = [
  RETAILERS.CHONDRUS,
  RETAILERS.CETOINE,
];

const isBisCampaignRetailer = (retailerName: string | null): boolean =>
  !!retailerName && BIS_CAMPAIGN_RETAILERS.includes(retailerName);

export const fakeBackendInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const { url, method } = request;

  /**
   * @returns Response depending on API called
   */
  function handleRoute(): Observable<HttpEvent<unknown>> {
    const matches: {
      getCampaigns: RegExpExecArray | null;
      getTotalReward: RegExpExecArray | null;
      getLastPurchase: RegExpExecArray | null;
      hasPlayedRecently: RegExpExecArray | null;
      getProducts: RegExpExecArray | null;
      getEligibleProduct: RegExpExecArray | null;
      getCoupons: RegExpExecArray | null;
      getYearlyReward: RegExpExecArray | null;
      getChallenges: RegExpExecArray | null;
      getAbTestData: RegExpExecArray | null;
      getRewards: RegExpExecArray | null;
      getPurchasedRewards: RegExpExecArray | null;
      activateReward: RegExpExecArray | null;
      postSelect: RegExpExecArray | null;
      loadCoupon: RegExpExecArray | null;
      postUnselect: RegExpExecArray | null;
      postClaim: RegExpExecArray | null;
      postSignin: RegExpExecArray | null;
      getAndPostOptin: RegExpExecArray | null;
    } = {
      getCampaigns:
        /core\/campaigns\?campaign_type=([\w=&-]+)(?:\?[\w=&-]+)?$/.exec(url),
      getTotalReward:
        /core\/customers\/([a-z0-9]+)\/campaigns\/([0-9]+)\/total-reward(?:\?[\w=&-]+)?$/.exec(
          url
        ),
      getLastPurchase:
        /core\/customers\/([a-z0-9]+)\/last-purchase(?:\?[\w=&-]+)?$/.exec(url),
      hasPlayedRecently:
        /core\/customers\/([a-z0-9]+)\/has-played-recently(?:\?[\w=&-]+)?$/.exec(
          url
        ),
      getProducts:
        /core\/product-scopes\/([a-z0-9]+)\/products(?:\?[\w=&-]+)?$/.exec(url),
      getEligibleProduct:
        /campaigns\/(\d+)\/offers\/(\d+)\/products(?:\?[\w=&%-]+)?$/.exec(url),
      getChallenges:
        /core\/customers\/([a-z0-9]+)\/campaigns\/([0-9]+)\/challenges(?:\?[\w=&-]+)?$/.exec(
          url
        ),
      getAbTestData:
        /campaigns\/([0-9]+)\/abtest\/([a-z0-9]+)(?:\?[\w=&-]+)?$/.exec(url),

      getRewards:
        /customers\/([a-z0-9]+)\/burn-rewards\/available(?:\?[\w=&-]+)?$/.exec(
          url
        ),
      getPurchasedRewards:
        /customers\/([a-z0-9]+)\/burn-rewards\/activated(?:\?[\w=&-]+)?$/.exec(
          url
        ),
      activateReward:
        /customers\/([a-z0-9]+)\/burn-rewards\/([a-z0-9]+)\/activate(?:\?[\w=&-]+)?$/.exec(
          url
        ),

      getCoupons:
        /customers\/([a-z0-9]+)\/campaigns\/([0-9]+)\/coupons(?:\?[\w=&-]+)?$/.exec(
          url
        ),
      getYearlyReward:
        /customers\/([a-z0-9]+)\/yearly-reward(?:\?[\w=&-]+)?$/.exec(url),
      postSelect: /challenges\/(\d+)\/select(?:\?[\w=&-]+)?$/.exec(url),
      loadCoupon:
        /customers\/([a-z0-9]+)\/campaigns\/([0-9]+)\/coupons\/([0-9]+)\/activate(?:\?[\w=&-]+)?$/.exec(
          url
        ),
      postUnselect: /challenges\/(\d+)\/unselect(?:\?[\w=&-]+)?$/.exec(url),
      postClaim: /customers\/([a-z0-9]+)\/claim(?:\?[\w=&-]+)?$/.exec(url),
      postSignin: /signin(?:\?[\w=&-]+)?$/.exec(url),
      getAndPostOptin: /optins\/([a-z0-9]+)(?:\?[\w=&-]+)?$/.exec(url),
    };
    switch (true) {
      case matches.getCampaigns && method === 'GET': {
        const campaignType = (matches.getCampaigns as RegExpExecArray)[1];
        if (campaignType === CampaignTypes.EVENT) {
          return of(
            new HttpResponse({
              status: 200,
              body: generateCampaigns(CampaignTypes.EVENT),
            })
          );
        } else if (campaignType === CampaignTypes.LOYALTYCHALLENGE_BIS) {
          return of(
            new HttpResponse({
              status: 200,
              body: generateCampaigns(CampaignTypes.LOYALTYCHALLENGE_BIS),
            })
          );
        } else if (campaignType === CampaignTypes.LOYALTYCHALLENGE) {
          return of(
            new HttpResponse({
              status: 200,
              body: generateCampaigns(CampaignTypes.LOYALTYCHALLENGE),
            })
          );
        } else {
          return of(
            new HttpResponse({
              status: 200,
              body: generateCampaigns(CampaignTypes.PERSONALIZED_PROMOTIONS),
            })
          );
        }
      }

      case matches.getTotalReward && method === 'GET':
        // eslint-disable-next-line no-case-declarations
        const campaignTotalRewards: { [key: number]: number } = {
          100: 30.52,
          101: 35.75,
          102: 38.8,
          103: 10,
          200: 20,
          201: 45.5,
          202: 50.5,
        };
        // eslint-disable-next-line no-case-declarations
        const campaignId: number = +matches.getTotalReward[2];
        return of(
          new HttpResponse({
            status: 200,
            body: {
              total_reward: campaignTotalRewards[campaignId],
            },
          })
        );

      case matches.getLastPurchase && method === 'GET':
        return of(
          new HttpResponse({
            status: 200,
            body: getMonthDates().lastDay.toISOString(),
          })
        );

      case matches.getAbTestData && method === 'GET': {
        const hints = [
          AmountLeftHint.DEFAULT,
          AmountLeftHint.GLOBAL_TIP,
          AmountLeftHint.LEFT_TO_SPEND_PER_CHALLENGE,
        ];
        const untienotsId = matches.getAbTestData[2];
        const hash = [...untienotsId].reduce(
          (acc, char) => acc + char.charCodeAt(0),
          0
        );
        const value = hints[hash % hints.length];
        const retailerName = environment.retailerName;
        // Every mocked shopper is in the "swipe" onboarding group: the overlay
        // only shows for retailers with `components.onboarding` and unrelated
        // specs seed the "onboarding-seen" flag to opt out.
        // `amount_left_hint` is dev-only: under Playwright it would inject the
        // Shane hint blocks into visual baselines unrelated to the onboarding
        // (the experiment failed and is due for removal anyway).
        if (
          retailerName === RETAILERS.SHANE &&
          environment.env !== ENVIRONMENTS.PLAYWRIGHT
        ) {
          return of(
            new HttpResponse({
              status: 200,
              body: {
                amount_left_hint: value,
                [ONBOARDING_AB_TEST_KEY]: OnboardingAbTestVariant.SWIPE,
              },
            })
          );
        }
        return of(
          new HttpResponse({
            status: 200,
            body: {
              [ONBOARDING_AB_TEST_KEY]: OnboardingAbTestVariant.SWIPE,
            },
          })
        );
      }

      case matches.hasPlayedRecently && method === 'GET':
        // eslint-disable-next-line no-case-declarations
        const hasPlayedRecently =
          (matches.hasPlayedRecently as RegExpExecArray)[1] !== 'step1';
        return of(
          new HttpResponse({
            status: 200,
            body: { has_played_recently: hasPlayedRecently },
          })
        );

      case matches.getProducts && method === 'GET':
        return of(new HttpResponse({ status: 200, body: generateProducts() }));

      case matches.getEligibleProduct && method === 'GET': {
        const barcode: string = request.params.get('barcode') ?? '';
        const product = generateEligibleProduct(barcode);
        if (!product) {
          // The barcode isn't part of the offer's scope/category: the real
          // backend answers 404, which the caller reads as "not eligible".
          return throwError(
            () =>
              new HttpErrorResponse({
                url,
                status: HttpStatusCode.NotFound,
                statusText: 'Not Found',
              })
          );
        }
        return of(new HttpResponse({ status: 200, body: product }));
      }

      case matches.getAndPostOptin && method === 'GET':
        return of(new HttpResponse({ status: 200, body: { optin: false } }));

      case matches.getAndPostOptin && method === 'POST':
        return of(
          new HttpResponse({ status: 200, body: { CR: 0, MSG: 'Succès' } })
        );

      case matches.getChallenges && method === 'GET':
        // Playwright-only: allow forcing a single frequency challenge sub_type
        // via localStorage key `mockFrequencySubType`.
        // This is used to snapshot each design variant (e.g. CETOINE).
        try {
          if (
            environment.env === ENVIRONMENTS.PLAYWRIGHT &&
            typeof localStorage !== 'undefined'
          ) {
            const forced = localStorage.getItem('mockFrequencySubType');
            if (
              forced &&
              (Object.values(SubType) as string[]).includes(forced)
            ) {
              const params = new FullResponseParams('default', false, false);
              params.step.nbToGenerate = 0;
              params.sponsor.nbToGenerate = 0;
              params.event.nbToGenerate = 0;
              params.frequency.nbToGenerate = 1;
              params.frequency.nbSelected = 1;
              const rawProgress = localStorage.getItem('mockFrequencyProgress');
              const progress = rawProgress ? Number(rawProgress) : 2;
              params.frequency.progress = Number.isFinite(progress)
                ? progress
                : 2;
              params.frequency.subType = forced as SubType;

              return of(
                new HttpResponse({
                  status: 200,
                  body: generateChallenges(params),
                })
              );
            }
          }
        } catch {
          // ignore
        }

        // Playwright-only: force every step challenge to one sub_type via
        // localStorage `mockStepSubType`, so a spec can pin the design it wants
        // to capture. DEV needs no hook — the mock rotates each retailer's step
        // sub types over the generated cards (`stepSubTypesByRetailer`), and
        // `/challenge-preview` renders any single card straight from the URL.
        //
        // The fixture follows the retailer: a bonus campaign for one that runs
        // one (its standard cards are forced back to DEFAULT, so only the bonus
        // page shows the design), the standard cards otherwise. The customer id
        // drives the state either way — `step1` not selected, `step3`
        // completed, anything else selected and in progress.
        try {
          if (
            environment.env === ENVIRONMENTS.PLAYWRIGHT &&
            typeof localStorage !== 'undefined'
          ) {
            const forced = localStorage.getItem('mockStepSubType');
            if (
              forced &&
              (Object.values(SubType) as string[]).includes(forced)
            ) {
              const customerId: string = (
                matches.getChallenges as RegExpExecArray
              )[1];
              const params = new FullResponseParams(
                STATUS_BY_CUSTOMER_ID[customerId] ?? 'default',
                false,
                false
              );
              params.frequency.nbToGenerate = 0;
              params.event.nbToGenerate = 0;
              params.step.subType = forced as SubType;

              return of(
                new HttpResponse({
                  status: 200,
                  body: generateChallenges(
                    params,
                    isBisCampaignRetailer(localStorage.getItem('retailerName'))
                  ),
                })
              );
            }
          }
        } catch {
          // ignore
        }

        if ((matches.getChallenges as RegExpExecArray)[2] === '2000') {
          return of(
            new HttpResponse({
              status: 200,
              body: generateChallenges(new FullResponseParams(undefined, true)),
            })
          );
        }
        if ((matches.getChallenges as RegExpExecArray)[1] === 'step1') {
          return of(
            new HttpResponse({
              status: 200,
              body: generateChallenges(new FullResponseParams('noSelections')),
            })
          );
        } else if ((matches.getChallenges as RegExpExecArray)[1] === 'step3') {
          return of(
            new HttpResponse({
              status: 200,
              body: generateChallenges(new FullResponseParams('allWon')),
            })
          );
        } else {
          return of(
            new HttpResponse({ status: 200, body: generateChallenges() })
          );
        }

      case matches.getRewards && method === 'GET':
        return of(
          new HttpResponse({
            status: 200,
            body: generateRewards(),
          })
        );

      case matches.getPurchasedRewards && method === 'GET':
        return of(
          new HttpResponse({
            status: 200,
            body: generateRewards({ isActive: true, length: 5 }),
          })
        );

      case matches.activateReward && method === 'POST': {
        const inputsArray = matches.activateReward as RegExpExecArray;
        const rewardId: number = +inputsArray[2];

        const fakeReward = generateRewards({ isActive: true, length: 1 })[0];
        fakeReward.reward_id = `${rewardId}`;

        return of(
          new HttpResponse({
            body: fakeReward,
            status: 200,
          })
        );
      }

      case matches.getCoupons && method === 'GET':
        if ((matches.getCoupons as RegExpExecArray)[1] === 'step1') {
          return of(
            new HttpResponse({
              status: 200,
              // Only "À découvrir": no activated nor finalized coupons.
              body: generateCoupons(
                new CouponsResponseParams({ notSelected: 24 })
              ),
            })
          );
        } else if ((matches.getCoupons as RegExpExecArray)[1] === 'step2') {
          return of(
            new HttpResponse({
              status: 200,
              // Everything activated, split between "En cours" and "Terminées".
              body: generateCoupons(
                new CouponsResponseParams({ selected: 12, redeemed: 12 })
              ),
            })
          );
        } else {
          return of(
            new HttpResponse({
              status: 200,
              body: generateCoupons(new CouponsResponseParams()),
            })
          );
        }

      case matches.getYearlyReward && method === 'GET':
        return of(
          new HttpResponse({ status: 200, body: { total_reward: 90 } })
        );

      case matches.loadCoupon && method === 'POST': {
        const inputsArray = matches.loadCoupon as RegExpExecArray;

        const selectedOfferId: number = +inputsArray[3];

        const selectedCouponResponse: {
          loading_date: string;
          offer_id: number;
        } = {
          offer_id: selectedOfferId,
          loading_date: new Date().toISOString(),
        };
        return of(
          new HttpResponse({ status: 200, body: selectedCouponResponse })
        );
      }

      case matches.postSelect && method === 'POST': {
        const selectedMissionId: number = +(
          matches.postSelect as RegExpExecArray
        )[1];
        const challenge_selected_response: {
          mission_id: number;
          selected_date: string;
        } = {
          mission_id: selectedMissionId,
          selected_date: new Date().toISOString(),
        };
        return of(
          new HttpResponse({ status: 200, body: challenge_selected_response })
        );
      }

      case matches.postUnselect && method === 'POST': {
        const unselectMissionId: number = +(
          matches.postUnselect as RegExpExecArray
        )[1];
        return of(
          new HttpResponse({
            status: 200,
            body: { mission_id: unselectMissionId, selected_date: null },
          })
        );
      }

      case matches.postClaim && method === 'POST':
        return of(new HttpResponse({ status: 200, body: {} }));

      case matches.postSignin && method === 'POST':
        return of(
          new HttpResponse({
            status: 200,
            body: {
              untienots_id:
                'eb8f28516867020a97083603679334bc234bd0d455afc0b3917204d72f3c533a',
            },
          })
        );

      default:
        // pass through any requests not handled above
        return next(request);
    }
  }

  // wrap in delayed observable to simulate server api call
  return of(null)
    .pipe(mergeMap(handleRoute))
    .pipe(materialize()) // call materialize and dematerialize to ensure delay even if an error is thrown (https://github.com/Reactive-Extensions/RxJS/issues/648)
    .pipe(delay(500))
    .pipe(dematerialize());
};
